import type { ThemeColor } from '@vrplatform/theme';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import {
  Center,
  Badge as MantineBadge,
  Loader,
  type MantineSize,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type { EmotionSx as Sx } from '@mantine/emotion';
import type {
  ComponentProps,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { LABEL_TOOLTIP_PROPS } from './LabelTooltip.styles';

type ButtonType = {
  component: 'button';
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};

type DivType = {
  component?: 'div';
  type?: undefined;
  disabled?: undefined;
  onClick?: undefined;
};

type BadgeSection = IconDefinition | ReactNode;
type BadgeAppearance = 'soft' | 'tag';

type BaseBadgeProps = {
  appearance?: BadgeAppearance;
  color?: ThemeColor;
  leftIcon?: BadgeSection;
  leftIconColor?: ComponentProps<typeof Icon>['color'];
  loading?: boolean;
  rightIcon?: BadgeSection;
  size?: MantineSize;
  sx?: Sx;
  tabIndex?: number;
  textColor?: string;
  variant?: ComponentProps<typeof MantineBadge<'div'>>['variant'];
  tooltip?: ReactNode;
  radius?: number | string;
} & (ButtonType | DivType);

export type BadgeProps = PropsWithChildren<BaseBadgeProps>;

const renderBadgeSection = ({
  color,
  icon,
  size = 12,
}: {
  color?: ComponentProps<typeof Icon>['color'];
  icon?: BadgeSection;
  size?: number;
}) => {
  if (!icon) return undefined;

  if (typeof icon === 'string') {
    return <Icon color={color} icon={icon as IconDefinition} size={size} />;
  }

  return <Center>{icon}</Center>;
};

export const Badge = ({
  appearance,
  color: badgeColor,
  disabled,
  leftIcon,
  leftIconColor,
  loading = false,
  rightIcon,
  children,
  component = 'div',
  type,
  size = 'md',
  sx = {},
  tabIndex,
  textColor,
  onClick,
  variant,
  tooltip,
  radius = 1000,
}: BadgeProps) => {
  const { colorScheme } = useMantineColorScheme();
  const { colors, primaryColor, white } = useMantineTheme();
  const color = badgeColor || primaryColor;
  const resolvedAppearance =
    appearance || (variant === 'white' ? 'tag' : 'soft');

  const isDarkTheme = colorScheme === 'dark';

  const resolvedTextColor =
    textColor ||
    (resolvedAppearance === 'tag'
      ? colors.neutral[6]
      : colors[color][isDarkTheme ? 4 : 9]);
  const resolvedBackgroundColor =
    resolvedAppearance === 'tag'
      ? white
      : isDarkTheme
        ? undefined
        : `${colors[color][1]}80!important`;
  const resolvedBorderColor =
    resolvedAppearance === 'tag'
      ? colors.neutral[3]
      : `${colors[color][isDarkTheme ? 1 : 2]}60`;
  const resolvedBorderWidth = resolvedAppearance === 'tag' ? 0.5 : undefined;
  const resolvedHeight = resolvedAppearance === 'tag' ? 22 : undefined;
  const resolvedPaddingInline = resolvedAppearance === 'tag' ? 6 : undefined;
  const resolvedFontWeight = resolvedAppearance === 'tag' ? 500 : undefined;

  const componentProps =
    component === 'button'
      ? {
          component,
          disabled: disabled || loading,
          onClick,
          type: type ?? 'button',
        }
      : {
          component,
        };

  return (
    <Tooltip
      {...LABEL_TOOLTIP_PROPS}
      disabled={!tooltip}
      events={{ focus: true, hover: true, touch: false }}
      label={tooltip}
    >
      <MantineBadge
        {...componentProps}
        color={color}
        tabIndex={tabIndex}
        variant={resolvedAppearance === 'tag' ? 'white' : variant}
        size={size}
        radius={radius}
        sx={(theme, utils) =>
          Object.assign(
            {
              cursor:
                component === 'button' && !disabled && onClick
                  ? 'pointer'
                  : undefined,
              paddingInline: resolvedPaddingInline,
              fontWeight: resolvedFontWeight,
              textTransform: 'none',
              color: resolvedTextColor,
              ':active':
                component === 'button'
                  ? {
                      transform: 'translateY(1px)',
                    }
                  : {},
            },
            typeof sx === 'function' ? sx(theme, utils) : sx
          )
        }
        styles={{
          root: {
            backgroundColor: resolvedBackgroundColor,
            borderColor: resolvedBorderColor,
            borderWidth: resolvedBorderWidth,
            height: resolvedHeight,
          },
        }}
        leftSection={renderBadgeSection({
          color: leftIconColor,
          icon: loading ? (
            <Loader color={resolvedTextColor} size={12} />
          ) : (
            leftIcon
          ),
        })}
        rightSection={renderBadgeSection({
          icon: rightIcon,
        })}
      >
        {children}
      </MantineBadge>
    </Tooltip>
  );
};
